/**
 * SortBy — compact dropdown that drives a sort key + direction for any
 * list page. Persists choice via the user-preferences API (so the same
 * admin gets the same sort on phone + laptop) with a localStorage cache
 * for instant first paint.
 *
 * Pass an `options` array of { value: 'key:asc' | 'key:desc', label }
 * and the parent receives the choice via `onChange({ key, dir })`.
 */
import React, { useEffect, useState } from 'react';
import { getCachedPreference, setUserPreference } from '../services/api';

export const SortBy = ({ storageKey, defaultValue, options, onChange, testId = 'sort-by' }) => {
  const [value, setValue] = useState(() => {
    const cached = getCachedPreference(storageKey);
    if (cached && options.some((o) => o.value === cached)) return cached;
    return defaultValue;
  });

  // Persist on change (cache-first then server) and notify parent.
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    const [key, dir] = value.split(':');
    onChange?.({ key, dir });
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setUserPreference(storageKey, value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div
      className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-2"
      data-testid={`${testId}-row`}
    >
      <label htmlFor={`${testId}-select`} className="text-xs text-slate-500 shrink-0">Sort by</label>
      <select
        id={`${testId}-select`}
        className="flex-1 h-8 text-sm border border-slate-200 rounded px-2 bg-white"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
};

/**
 * Build a comparator function from a `{key, dir}` choice and a getter
 * that maps a row → the value to compare on. Strings compare with
 * locale + numeric awareness so INV-2510 > INV-2501.
 */
export const buildComparator = ({ key, dir }, getter) => {
  const mult = dir === 'asc' ? 1 : -1;
  return (a, b) => {
    const va = getter(a, key);
    const vb = getter(b, key);
    if (typeof va === 'string' && typeof vb === 'string') {
      return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) * mult;
    }
    if ((va ?? 0) < (vb ?? 0)) return -1 * mult;
    if ((va ?? 0) > (vb ?? 0)) return 1 * mult;
    return 0;
  };
};

export default SortBy;
